<template>
    <section class="container">
      <div class="container__wrapper">

        <!-- Header -->
        <header class="container__header desktop-view">
          <div class="container__column container__column--left">
            <!-- content for left column goes here -->
            <h2 class="container__column__title ignore-design">SYNCROSUPPLY <br />CENTRAL</h2>
            <div class="container__column__logo-small">
              <img src="@/assets/images/INFORM_RGB-HZtransparent.a1efd08b.png" alt="Inform Logo" />
            </div>
          </div>
          <div class="container__column container__column--right">
            <!-- content for right column goes here -->
            <div class="container__column__logo-main">
              <img v-if="isTablet" src="@/assets/images/logo-tablet.svg" alt="Central" />
              <img v-else src="@/assets/images/Logo-Central.svg" alt="Central">
            </div>
            <div
              class="container__column__language-dropdown"
              v-if="!isSuccessResponse && !isErrorResponse"
            >
              <LanguageSelect @language-selected="handleLanguageSelected" />
            </div>
          </div>
        </header>

        <div
          v-if="!isSuccessResponse && !isErrorResponse"
          class="container__body"
        >

          <h3 class="container__title ignore-design">{{ ($t('formTitle')) }}</h3>

          <!-- Navigation Steps Component -->
          <NavSteps
            :step="step"
            :allowedStep="allowedStep"
            @emitFirstStep="navigateToFirstStep"
            @emitSecondStep="navigateToSecondStep"
            @emitThirdStep="navigateToThirdStep"
          />

          <!-- section 1 -->
          <transition name="fade">
            <section v-show="step === 1" class="container__section">

              <form
                @submit.prevent
                class="container__form"
                autocomplete="off"
              >
                <FirstNameInput v-model="userData.firstName" />
                <LastNameInput v-model="userData.lastName" />

                <div class="spacing"></div>

                <EmailInput v-model="userData.email" />
                <TelInputSelect
                  v-model="userData.telephone"
                  class="container__form__telInput"
                />
              </form>

              <!-- footer-fixed-content -->
              <div class="container__fixed-content">
                <!-- nav buttons wrapper -->
                <div class="container__buttons">
                  <FormButton
                    id="step1-next"
                    class="next"
                    :label="$t('buttonsNavigation.next')"
                    :disabled="isUserDataSubmitAllowed"
                    :onClick="nextStep"
                  />
                </div>

                <!-- External link to login page -->
                <Link />
              </div>

            </section>
          </transition>

          <!-- section 2 -->
          <transition name="fade">
            <section v-show="step === 2" class="container__section">
              <form
                @submit.prevent
                class="container__form"
                autocomplete="off"
              >
                <CompanyNameInput v-model="companyData.company" />

                <div class="spacing"></div>

                <!-- Country select -->
                <div>
                  <p
                    class="container__form__country-label ignore-design"
                  >
                    {{ ($t('companyFormData.country')) }} *
                  </p>

                  <CountrySelect
                    v-model="companyData.countryCode"
                    class="container__form__country-select"
                    name="country-select"
                  />

                </div>

                <div class="container__flex-wrapper">
                  <StreetNameInput
                    class="container__flex-wrapper--first-input"
                    v-model="companyData.streetName"
                  />
                  <HouseNumberInput
                    class="container__flex-wrapper--last-input"
                    v-model="companyData.houseNumber"
                  />
                </div>

                <div class="container__flex-wrapper">
                  <PostalCodeInput
                    class="container__flex-wrapper--last-input"
                    v-model="companyData.postalCode"
                  />
                  <CityNameInput
                    class="container__flex-wrapper--first-input"
                    v-model="companyData.city"
                  />
                </div>

                <time-zone-select
                  v-model="companyData.timeZone"
                  @update:selectedTimezone="updateTimezone"
                />
              </form>

              <!-- nav buttons wrapper -->
              <div class="container__buttons">
                <FormButton
                  class="previous ignore-design"
                  :label="$t('buttonsNavigation.previous')"
                  :disabled="false"
                  :onClick="previousStep"
                />
                <FormButton
                  id="step2-next"
                  class="next"
                  :label="$t('buttonsNavigation.next')"
                  :disabled="isCompanyDataSubmitAllowed"
                  :class="{ 'disabled': isCompanyDataSubmitAllowed }"
                  :onClick="nextStep"
                />
              </div>

              <!-- External link to login page -->
              <Link />

            </section>
          </transition>

          <!-- section 3 -->
          <transition name="fade">
            <section v-show="step === 3">

              <div class="container__form">
                <div class="container__privacy">
                  <div
                    v-for="(item, index) in items" :key="index"
                    class="container__privacy-wrapper"
                  >
                    <input
                      type="checkbox"
                      v-model="checkedPrivacy"
                      :value="item"
                      :id="item.id"
                      :class="{ 'important-checked': item.id === 'termsOfUse' && checkedPrivacy.includes(item) }"
                    >
                    <span
                      v-html="($t(item.label))"
                      class="container__privacy__label"
                    >
                    </span>
                  </div>
                </div>
              </div>

              <!-- nav buttons wrapper -->
              <div class="container__buttons">
                <FormButton
                  class="previous ignore-design"
                  :label="$t('buttonsNavigation.previous')"
                  :disabled="false"
                  :onClick="previousStep"
                />
                <FormButton
                  id="step3-next"
                  class="next ignore-design"
                  :label="$t('buttonsNavigation.register')"
                  :disabled="!isPrivacyChecked"
                  :class="{ '.disabled': !isPrivacyChecked }"
                  :onClick="submitForm"
                />
              </div>

              <!-- External link to login page -->
              <Link />

            </section>
          </transition>

        </div>

        <template v-if="isSuccessResponse">
          <SuccessMessage :userData="userData" />
        </template>

        <!-- Error Message Component -->
        <div v-if="isErrorResponse">
          <ErrorMessage :message="errorMessage" :status="errorStatus"/>
        </div>

      </div>
    </section>
</template>
<script>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { languages } from '@/data/i18n'
import FirstNameInput from '@/components/FirstNameInput.vue'
import LastNameInput from '@/components/LastNameInput.vue'
import EmailInput from '@/components/EmailInput.vue'
import CompanyNameInput from '@/components/CompanyNameInput.vue'
import StreetNameInput from '@/components/StreetNameInput.vue'
import HouseNumberInput from '@/components/HouseNumberInput.vue'
import NavSteps from '@/components/NavSteps.vue'
import Link from '@/components/UI/Link.vue'
import useSignup from '@/composables/useSignup'
import userSubmit from '@/composables/useSubmit'
import PostalCodeInput from '@/components/PostalCodeInput.vue'
import CityNameInput from '@/components/CityNameInput.vue'
import SuccessMessage from '@/components/SuccesMessage.vue'
import ErrorMessage from '@/components/ErrorMessage.vue'
import TelInputSelect from '@/components/TelInputSelect.vue'
import LanguageSelect from '@/components/LanguageSelect.vue'
import FormButton from '@/components/UI/FormButton.vue'
import TimeZoneSelect from '@/components/TimeZoneSelect.vue'
import axios from 'axios'
import { countries } from '@/data/countriesData'
import CountrySelect from '@/components/CountrySelect.vue'

export default {
  components: {
    FirstNameInput,
    LastNameInput,
    EmailInput,
    CompanyNameInput,
    StreetNameInput,
    HouseNumberInput,
    NavSteps,
    Link,
    PostalCodeInput,
    CityNameInput,
    SuccessMessage,
    ErrorMessage,
    TelInputSelect,
    LanguageSelect,
    FormButton,
    TimeZoneSelect,
    CountrySelect
  },
  setup () {
    const { t } = useI18n({ useScope: 'global' })

    const isTablet = ref(false)
    const errorMessage = ref(null)
    const errorStatus = ref(null)
    const route = useRoute()

    // Countrylist default country
    const country = ref('DE')

    onMounted(() => {
      // mounted to detect viewport for a logo switching
      const tabletQuery = window.matchMedia('(min-width: 767px)')
      isTablet.value = tabletQuery.matches
      tabletQuery.addListener(() => {
        isTablet.value = tabletQuery.matches
      })

      let urlLanguage = route.query.language
      if (urlLanguage) {
        urlLanguage = urlLanguage.toLowerCase()

        if (Object.keys(languages).includes(urlLanguage)) {
          handleLanguageSelected(urlLanguage)
        }
      }
    })

    const instance = getCurrentInstance()
    const fallbackLocale = instance.proxy.$i18n.fallbackLocale

    const handleLanguageSelected = (selectedLanguage) => {
      userData.language = selectedLanguage || fallbackLocale
      instance.proxy.$i18n.locale = userData.language
    }

    // user-data obj
    const userData = reactive({
      firstName: '',
      lastName: '',
      email: '',
      telephone: '',
      language: fallbackLocale
    })

    // company-data obj
    const companyData = reactive({
      company: '',
      countryCode: country,
      streetName: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      timeZone: ''
    })

    // Method to update the companyData.timeZone property
    const updateTimezone = (value) => {
      companyData.timeZone = value
    }

    // PRIVACY
    const items = reactive([
      {
        id: 'termsOfUse',
        label: 'privacy.label1'
      },
      {
        id: 'downtimeNotification',
        label: 'privacy.label2'
      },
      {
        id: 'informationNotification',
        label: 'privacy.label3'
      }
    ])

    // Privacy checkboxes array
    const checkedPrivacy = ref([])

    // getting values(errorObj, isButtonDisabled) with destructuring
    const { errorObj } = useSignup()
    const { isUserDataSubmitAllowed, isCompanyDataSubmitAllowed } = userSubmit(userData, companyData, errorObj)

    // step - by default: 1
    const step = ref(1)

    // isSuccessMessage
    const isSuccessResponse = ref(false)
    const isErrorResponse = ref(false)

    // method to move on to the next step
    const nextStep = () => {
      step.value++
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    // method to move back to the previous step
    const previousStep = () => {
      step.value--
    }
    // submit form
    const submitForm = () => {
      const privacy = {
        informationNotification: checkedPrivacy.value.some(item => item.id === 'informationNotification'),
        downtimeNotification: checkedPrivacy.value.some(item => item.id === 'downtimeNotification'),
        termsOfUse: checkedPrivacy.value.some(item => item.id === 'termsOfUse')
      }

      const data = { ...companyData, ...userData, privacy }

      // Creating a new object with trimmed values
      const trimmedData = Object.fromEntries(
        Object.entries(data).map(([key, value]) => {
          return [key, typeof value === 'string' ? value.trim() : value]
        })
      )

      // Define a ValidateStatus func
      const customAxios = axios.create({
        validateStatus: function (status) {
          return status >= 200 && status < 500 // Accept only status codes between 200 and 500
        }
      })

      // Making a request
      customAxios.post(process.env.VUE_APP_API_URL + '/service/registration', trimmedData)
        .then((response) => {
          // if success
          if (response.status === 200) {
            isSuccessResponse.value = true
          // conditionally error handling
          } else if (response.status === 400) {
            isErrorResponse.value = true
            if (response.data && response.data.message === '::error.registration.email') {
              errorStatus.value = response.status
              errorMessage.value = t('errorPage.400.title')
            }
          } else if (response.status === 404) {
            isErrorResponse.value = true
            if (response.data) {
              errorStatus.value = response.status
              errorMessage.value = t('errorPage.404.title')
            }
          } else if (response.status === 502) {
            isErrorResponse.value = true
            if (response.data) {
              errorStatus.value = response.status
              errorMessage.value = t('errorPage.502.title')
            }
          } else if (response.status === 504) {
            isErrorResponse.value = true
            if (response.data) {
              errorStatus.value = response.status
              errorMessage.value = t('errorPage.504.title')
            }
          } else if (response.status === 503) {
            isErrorResponse.value = true
            if (response.data) {
              errorStatus.value = response.status
              errorMessage.value = t('errorPage.503.title')
            }
          }
        })
        .catch((error) => {
          console.log('Inside catch block', error)
        })
    }

    const navigateToFirstStep = () => {
      step.value = 1
    }
    const navigateToSecondStep = () => {
      if (isUserDataSubmitAllowed.value === false) {
        step.value = 2
      }
    }
    const navigateToThirdStep = () => {
      if (isUserDataSubmitAllowed.value === false && isCompanyDataSubmitAllowed.value === false) {
        step.value = 3
      }
    }

    // computed func to allowe top navigation steps
    const allowedStep = computed(() => {
      if (isUserDataSubmitAllowed.value === false && isCompanyDataSubmitAllowed.value === false) {
        return 3
      }
      if (isUserDataSubmitAllowed.value === false) {
        return 2
      }
      return 1
    })

    // computed function to allow register btn depends on privacy checked
    const isPrivacyChecked = computed(() => {
      const isImportantChecked = checkedPrivacy.value.find((item) => item.id === 'termsOfUse')

      return !!isImportantChecked
    })

    return {
      userData,
      companyData,
      updateTimezone,
      nextStep,
      previousStep,
      submitForm,
      step,
      isUserDataSubmitAllowed,
      isCompanyDataSubmitAllowed,
      checkedPrivacy,
      isPrivacyChecked,
      isSuccessResponse,
      isErrorResponse,
      navigateToFirstStep,
      navigateToSecondStep,
      navigateToThirdStep,
      allowedStep,
      items,
      isTablet,
      handleLanguageSelected,
      country,
      errorMessage,
      errorStatus,
      t,
      countriesList: countries
    }
  }
}
</script>
<style lang="scss">
.container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100%;

  &__wrapper {
    width: 100%;
    background-color: $background-color-primary;
    padding: 6px 16px 25px 16px;
    overflow: hidden;
    flex-grow: 1;

    @include respond-to('medium') {
      padding-top: calc(var(--spacing-l) * 2);
    }
  }

  &__section {
    contain: content;
  }

  // Header section
  &__header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 40px;
    height: auto;
    width: 100%;
    height: 95px;

    // small: min-width media query begins from 767px
    @include respond-to('small') {
      margin: 0 auto;
      height: auto;
      margin-bottom: 100px;
    }
  }

  &__body {
    @include respond-to('small') {
      max-width: 532px;
      margin: 0 auto;
    }
  }

  &__column {

    &--left {
      text-align: left;
      width: 60%;
      padding-top: 14px;
    }

    &--right {
      text-align: right;
      position: relative;
      max-width: 200px;
      min-width: 130px;

      // small: min-width media query begins from 767px
      @include respond-to('small') {
        max-width: 265px;
      }
    }

    // header top-left heading
    &__title {
      margin-bottom: 12px;
      @include header-title();

      // small: min-width media query begins from 767px
      @include respond-to('small') {
        margin-bottom: calc(var(--spacing-l) * 2);
      }
    }

    // header bottom-left logo
    &__logo-small {
      max-width: 78px;
      height: auto;
      position: relative;
      display: flex;

      // small: min-width media query begins from 767px
      @include respond-to('small') {
        max-width: 191px;
      }

      img {
        width: 100%;
      }
    }

    &__logo-main {
      position: relative;
      right: -17px;
      top: -7px;
      max-width: 130px;

      // small: min-width media query begins from 767px
      @include respond-to('small') {
        max-width: 300px;
      }

      img {
        width: 100%;
      }
    }

    &__language-dropdown {
      font-size: var(--fontsize-xs);
    }
  }

  // form center heading
  &__title {
    margin-bottom: calc(var(--spacing-xl) + var(--spacing-s));
    @include form-title;

    // small: min-width media query begins from 767px
    @include respond-to('small') {
      margin-bottom: calc(var(--spacing-xl) * 3 + var(--spacing-xs));
    }
    @include respond-to('medium') {
      margin-bottom: calc(var(--spacing-xl) * 2 + var(--spacing-xs));
    }
  }
  // Form company data inputs
  &__flex-wrapper {
    display: flex;
    width: 100%;

    &--first-input {
      flex: auto;
      margin-right: 8px;
      max-width: 70%;
    }
    &--last-input {
      flex: 1;
      max-width: 30%;
    }
    + div {
      .container__flex-wrapper--last-input {
        margin-right: 8px;
      }
      .container__flex-wrapper--first-input {
        margin-right: 0;
      }
    }
  }

  &__fixed-content {
    //position: fixed;
    bottom: 10px;
    width: 100%;

    @include respond-to('small') {
      position: relative;
      bottom: auto;
    }
  }

  &__buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;

    // small: min-width media query begins from 767px
    @include respond-to('small') {
      justify-content: center;
    }
  }

  .next,
  .previous {
    //@include form-button;
    width: calc(48% + 10px);
    min-width: auto !important;

    // small: min-width media query begins from 767px
    @include respond-to('small') {
      width: calc(48% + 10px);
      height: 48px;
    }
  }

  .next {
    // only button-next in step 1: mobile width = 100%
    &#step1-next {
      width: 100%;

      @include respond-to('small') {
        width: calc(48% + 10px);
      }
    }
  }

  .previous {
    margin-right: 8px;
  }

  &__form {
    width: 100%;
    margin: auto;
    border-radius: 3px;
    margin-bottom: calc(var(--spacing-xl) + var(--spacing-s));

    @include respond-to('small') {
      margin-bottom: 112px;
    }

    .input-wrapper {
      margin-bottom: 12px;
    }
    .spacing {
      width: 100%;
      height: var(--spacing-m);

      @include respond-to('small') {
        height: calc(var(--spacing-m) * 4);
      }
    }
    &__telInput {
      z-index: 10;
      position: relative;
    }
    &__country-label {
      text-align: left;
      @include input-label;
    }
    &__country-select {
      display: block;
      width: 100%;
      border: 1px solid $border-color-primary;
      border-radius: 3px;
      padding: 10px 12px;
      box-sizing: border-box;
      font-weight: var(--fontweight-regular);
      transition: 0.3s ease;
      height: 2.75rem;
      background-color: var(--white0);
      color: var(--black950);
      outline: none;
      margin-bottom: 12px;
    }
  }

  .nav-steps {
    margin-bottom: calc(var(--spacing-xl) + var(--spacing-s));

    @include respond-to('small') {
      margin-bottom: 5rem;
    }
  }

  &__privacy {
    text-align: left;
    margin-bottom: var(--spacing-xl);

    div {
      display: inline-flex;
      margin-bottom: var(--spacing-m);
      min-height: 25px;

      input[type='checkbox'] {
        @include checkbox;

        &#termsOfUse {
          background: rgba(130, 35, 35, 0.2);
          border: 1px solid var(--color-error);
        }
      }
    }

    &__label {
      @include checkbox-label;

      margin-left: 16px;
      margin-top: -1px;
      flex: 1;

      a {
        font-weight: var(--fontweight-bold);
        text-decoration: underline;
      }
    }
  }

  /* Header desktop styles */
  @include respond-to('medium') {
    .container__header {
      display: none;
    }
    .desktop-view {
      max-width: 532px;
      display: flex;
      width: 100%;
      margin: 0 auto;
      flex-direction: column;
      align-items: center;
      margin-bottom: calc(var(--spacing-xl) * 2 + var(--spacing-xs));

      .container__column__logo-main {
        display: none;
      }
      .container__column--left {
        width: 100%;
        display: flex;
        justify-content: space-between;
      }
      .container__column__title {
        text-align: left;
        margin-bottom: 0;
      }
      .container__column__logo-small {
        max-width: 160px;
        display: block;
      }
      .language-select-wrapper {
        top: 70px;
        margin-right: unset;
        right: unset;
        left: 250px;
        width: 85px;
      }
    }
  }
}

// Section-transition
.fade-enter-active {
  transition: all .3s ease;
}

.fade-leave-active {
  display: none;
  transition: all .4s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.w-100 {
  width: 100%;
}

</style>
